import type { Site } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { createPalette, type PaletteCommandDef, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Dialog, DialogBody, DialogRoot, toast, useDialogTriggerState } from '@gitbook/spine-react';
import { assertIsDefined, type MaybePromise } from '@gitbook/util-ts';

import {
    isSiteUpgradeRequiredForFeature,
    SiteContextProvider,
    useTrialingSiteFeaturePlan,
} from '../../state';
import { getTrialingBadgeProps } from '../OrganizationBilling';
import { AdaptiveContentBuilderDialog } from './AdaptiveContentBuilderDialog';
import { AdaptiveContentFeatureIntro } from './AdaptiveContentFeatureIntro';
import { type AdaptiveContentTargetObjectType, getTextForTarget } from './targets';

type SiteAudienceInput = {
    organizationId: string;
    site: Site;

    target: AdaptiveContentTargetObjectType;

    readOnly?: boolean;

    condition: string | undefined;
    onChange: (condition: string | undefined) => MaybePromise<void>;
};

/**
 * Hook to get the palette commands in order to configure the site audience
 * on an object.
 */
export function useSiteAudienceCommand(input: SiteAudienceInput) {
    const { organizationId, site, target, readOnly = false, condition, onChange } = input;
    const isUpgradeRequired = isSiteUpgradeRequiredForFeature({
        site,
        featureKey: api.SiteFeatureKey.SitesAdaptiveContent,
    });
    const trialingPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesAdaptiveContent);

    const itemConditionDialog = useDialogTriggerState();
    const introDialog = useDialogTriggerState();

    const itemHasCondition = typeof condition !== 'undefined' && condition.length > 0;

    const editCommand: PaletteCommandDef = {
        key: 'audience.condition.edit',
        title: 'Edit condition',
        icon: Icon.Edit,
        trigger: () => {
            itemConditionDialog.open();
        },
    };

    const options: PaletteCommandDef[] = [
        {
            key: 'audience.default',
            title: 'Default',
            subtitle: 'Anyone on your site can access',
            icon: Icon.Globe,
            type: PaletteCommandType.RADIO,
            selected: !itemHasCondition,
            trigger: async () => {
                await toast.catch(async () => {
                    await onChange(undefined);
                    toast.success('Site audience updated');
                }, 'Error while updating condition');
            },
        },
        {
            key: 'audience.conditional',
            type: PaletteCommandType.RADIO,
            upgrade: isUpgradeRequired,
            badge: trialingPlan
                ? getTrialingBadgeProps({ plan: trialingPlan })
                : isUpgradeRequired
                  ? {
                        kind: 'upgrade',
                        label: 'Upgrade',
                    }
                  : undefined,
            title: 'Conditional',
            subtitle: 'Site visitors matching a condition',
            icon: Icon.Condition,
            selected: itemHasCondition,
            trigger: () => {
                if (isUpgradeRequired || !site.adaptiveContent?.enabled) {
                    return introDialog.open();
                }

                itemConditionDialog.open();
            },
        },
        ...(itemHasCondition ? [editCommand] : []),
    ];

    const audienceCommand: PaletteCommandDef | null = !readOnly
        ? {
              key: 'adaptiveContent.audience',
              title: 'Site audience',
              subtitle: `Choose who can access this ${getTextForTarget(target)} on your site`,
              icon: Icon.Condition,
              descendant: () => {
                  return createPalette({
                      static: true,
                      groups: [
                          {
                              key: 'audience',
                              commands: options,
                          },
                      ],
                  });
              },
          }
        : null;

    // If the audience command is not available, we show the condition command if the item has a condition
    const command: PaletteCommandDef | null = audienceCommand
        ? audienceCommand
        : condition
          ? {
                key: 'adaptiveContent.viewCondition',
                title: 'View condition',
                icon: Icon.Condition,
                trigger: () => {
                    itemConditionDialog.open();
                },
            }
          : null;

    const onSaveItemExpression = async (expr) => {
        await toast.catch(async () => {
            await onChange(expr);
        }, 'Error while updating condition');
    };

    return {
        command,
        options,
        dialogs: (
            <SiteContextProvider site={site} organizationId={organizationId}>
                <DialogRoot key="adaptive-intro-dialog" {...introDialog.rootProps}>
                    <Dialog>
                        <DialogBody>
                            <AdaptiveContentFeatureIntro
                                orientation="vertical"
                                onFeatureEnabled={() => {
                                    analytics.logEvent(
                                        analytics.createSiteEvent(
                                            {
                                                organization: organizationId,
                                                id: site.id,
                                            },
                                            {
                                                type: 'adaptive_content_enable',
                                                source: target,
                                            }
                                        )
                                    );
                                    introDialog.close();
                                    itemConditionDialog.open();
                                }}
                            />
                        </DialogBody>
                    </Dialog>
                </DialogRoot>

                <DialogRoot key="condition-dialog" {...itemConditionDialog.rootProps}>
                    <AdaptiveContentBuilderDialog
                        initialTab="expression"
                        organizationId={organizationId}
                        siteId={site.id}
                        tabs={
                            readOnly
                                ? [
                                      {
                                          tab: 'expression',
                                          target: 'page',
                                          expression: condition,
                                          readOnly,
                                      },
                                  ]
                                : [
                                      {
                                          tab: 'expression',
                                          target,
                                          expression: condition || undefined,
                                          onSaveExpression: onSaveItemExpression,
                                      },
                                      { tab: 'schema' },
                                  ]
                        }
                    />
                </DialogRoot>
            </SiteContextProvider>
        ),
        onClickCondition: () => {
            assertIsDefined(condition);
            itemConditionDialog.open();
        },
    };
}
